/** @format */

import { Localized } from "@fluent/react";
import moment from "moment";
import { useEffect, useState } from "react";
import { functionHandler } from "@components/notfy";

export function useFilterAttendees(attendees = [], filter = "", tags = []) {
	const filterByFilter = (attendees, filter) => {
		const filtered = attendees.filter(att => {
			const full_name = att.type === "authenticated" ? `${att.first_name} ${att.last_name}` : att.username;
			return (
				full_name.toLowerCase().includes(filter.toLowerCase()) ||
				att.company_name?.toLowerCase().includes(filter.toLowerCase()) ||
				att.title?.toLowerCase().includes(filter.toLowerCase())
			);
		});
		return filtered;
	};

	const filterByTags = (attendees, tags) => {
		const inGroup = attendees.filter(a => a.groups?.length > 0);
		const filtered = inGroup.filter(a => {
			const inEveryFilterGroup = tags.every(tag => a.groups.includes(tag));
			return inEveryFilterGroup;
		});

		return filtered;
	};

	if (!filter && tags.length === 0) {
		return attendees;
	} else if (!filter) {
		return filterByTags(attendees, tags);
	} else if (tags.length === 0) {
		return filterByFilter(attendees, filter);
	} else {
		const filteredByFilter = filterByFilter(attendees, filter);
		return filterByTags(filteredByFilter, tags);
	}
}

/* onClick={() =>
	functionHandler(
		() => removeSection(canvasRef.current, headers, venue, apiBatch),
		"removeSection"
	)
} */

export function useSearchByProps(list = [], propKeys = null, className, placeholder, localizedId, attrs) {
	const [filtered, setFiltered] = useState(list);
	const [searchTerm, setSearchTerm] = useState("");
	const handleChange = value => {
		setSearchTerm(value);
		const filteredList = list.filter(elem => {
			if (propKeys) {
				return propKeys.some(prop =>
					elem[prop]
						? JSON.stringify(elem[prop])
								.toLowerCase()
								.includes(value.toLowerCase())
						: false
				);
			} else
				return elem
					? JSON.stringify(elem)
							.toLowerCase()
							.includes(value.toLowerCase())
					: false;
		});
		setFiltered(filteredList);
	};
	const input = (
		<Localized id={localizedId} attrs={attrs}>
			<input
				className={className}
				value={searchTerm}
				placeholder={placeholder}
				onChange={functionHandler(e => handleChange(e.target.value), "handleChange")}
			/>
		</Localized>
	);
	return [filtered, input];
}

export function useMessageTimeOut(timedOut, time) {
	let timer = null; // eslint-disable-line no-unused-vars
	const [isTimedOut, setIsTimedOut] = useState();

	useEffect(() => {
		if (timedOut) {
			setIsTimedOut(timedOut);
			timer = setTimeout(() => {
				// eslint-disable-line react-hooks/exhaustive-deps
				setIsTimedOut(false);
			}, time);
		}
	}, [timedOut]);

	return [isTimedOut];
}

export function useSilenceTimer(silenced, silencedTime, stopSilence) {
	let timer = null;
	const [isSilenced, setIsSilenced] = useState();
	const [timeUntilUnmuted, setTimeUntilUnmuted] = useState({ hours: 0, minutes: 0, seconds: 0 });

	useEffect(() => {
		if (silenced) {
			setIsSilenced(silenced);
			timer = setInterval(() => {
				// eslint-disable-line react-hooks/exhaustive-deps
				setTimeUntilUnmuted(moment().countdown(silencedTime));

				if (
					moment()
						.utc()
						.local()
						.isAfter(silencedTime)
				) {
					clearInterval(timer);
					setIsSilenced(false);
					stopSilence();
				}
			}, 1000);
		}
	}, [silenced]);

	return [isSilenced, timeUntilUnmuted];
}

export function useUpdateNotifications(inbox, user_id) {
	const [notifications, setNotifications] = useState(0);

	useEffect(() => {
		if (inbox.private_messages) {
			let counter = 0;
			for (let priv of inbox.private_messages) {
				if (priv.preview_message && !priv.preview_message.seen) {
					counter++;
				}
				if (
					priv.chat_history.length > 0 &&
					priv.chat_history[priv.chat_history.length - 1].sender_id === user_id
				) {
					counter--;
				}
			}

			setNotifications(counter);
		}
	}, [inbox]); // eslint-disable-line react-hooks/exhaustive-deps
	return notifications;
}

export function useHandleCooldown(slowmode, guestuser, active_channel) {
	const [cooldown, setCooldown] = useState(0);
	const calculateCooldown = () => {
		const cd = slowmode?.[active_channel] === null ? slowmode?.global : slowmode?.[active_channel];
		if (guestuser && (cd < 5 || cd === undefined)) return 5;
		return cd;
	};
	useEffect(() => {
		setCooldown(calculateCooldown());
	}, [slowmode]);

	const [timeOutTime, setTimeOutTime] = useState(cooldown * 1000);
	const [timedOut, setTimedOut] = useState(false);

	useEffect(() => {
		if (cooldown) setTimeOutTime(cooldown * 1000);
	}, [cooldown]);

	const handleTimeout = () => {
		if (guestuser || cooldown > 0) {
			setTimedOut(true);
			setTimeout(() => {
				setTimedOut(false);
			}, timeOutTime);
		}
	};
	return [cooldown, timeOutTime, timedOut, handleTimeout];
}
